import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    Chip,
    Card,
    CardContent,
    useMediaQuery,
    useTheme,
    Button,
    IconButton,
    Grid,
    CircularProgress
} from '@mui/material';
import { convertToRS } from 'core/js/FormUtils';
import AddIcon from '@mui/icons-material/Add';
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CancelIcon from '@mui/icons-material/Cancel';
import { getTransferreqInfoByUseridAPI } from 'components/transferreq/TransferreqAPI';  
import { useSelector } from 'react-redux';
import { transformDateValue } from 'core/js/FormUtils';
import { Divider } from '../../../../../node_modules/@mui/material/index';

const IST_TIMEZONE = 'Asia/Kolkata';

const parseApiDate = (value) => {
    if (!value) {
        return null;
    }
    const normalizedValue = /[zZ]|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`;
    const date = new Date(normalizedValue);
    return Number.isNaN(date.getTime()) ? null : date;
};

export default function BankTransferReq() {
    const [requestList, setRequestList] = useState([]);
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const {userid} = useSelector(state => state.headerslice.userprofileInfo);

    // Sample data - replace with actual API call
    useEffect(() => {
        setLoading(true);
        getTransferreqInfoByUseridAPI(
            userid,
            (response) => {
                if (response && Array.isArray(response)) {
                    // Sort by creationTimestamp in descending order (latest first)
                    const sortedList = response.sort((a, b) => {
                        const dateA = new Date(a.creationTimestamp || 0);
                        const dateB = new Date(b.creationTimestamp || 0);
                        return dateB - dateA; // Descending order
                    });
                    setRequestList(sortedList);
                } else {
                    setRequestList(response);
                }
                setLoading(false);
            },
            () => {
                setLoading(false);
            }
        );
    }, [userid]);

    const formatDate = (dateString) => {
        if (!dateString) return { date: '-', time: '-' };
        try {
            const date = parseApiDate(dateString);
            if (!date) {
                return { date: dateString, time: '-' };
            }
            return {
                date: date.toLocaleDateString('en-IN', {
                    timeZone: IST_TIMEZONE,
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit'
                }),
                time: date.toLocaleTimeString('en-IN', {
                    timeZone: IST_TIMEZONE,
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                })
            };
        } catch {
            return { date: dateString, time: '-' };
        }
    };

    const getStatusChip = (status) => {
        const statusConfig = {
            0: { 
                label: 'Pending', 
                color: 'warning', 
                icon: <PendingIcon sx={{ fontSize: 16 }} /> 
            },
            1: { 
                label: 'Initiated', 
                color: 'info', 
                icon: <PendingIcon sx={{ fontSize: 16 }} /> 
            },
            2: { 
                label: 'Transferred', 
                color: 'success', 
                icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> 
            },
            3: { 
                label: 'Rejected', 
                color: 'error', 
                icon: <CancelIcon sx={{ fontSize: 16 }} /> 
            }
        };

        const config = statusConfig[status] || statusConfig[0];

        return (
            <Chip
                label={config.label}
                color={config.color}
                size="small"
                icon={config.icon}
                sx={{ fontWeight: 600, fontSize: '0.75rem' }}
            />
        );
    };

    // Desktop Table View
    const DesktopTable = () => (
        <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
            <Table>
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.main' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 120 }}>
                            Status
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 120 }}>
                            Request #
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'right', minWidth: 140 }}>
                            Requested Amount
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 150 }}>
                            Request Date
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 150 }}>
                            Transfer Date
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {requestList && requestList.length > 0 ? (
                        requestList.map((request) => {
                            const requestDateTime = formatDate(request.requestDate);
                            const transferDateTime = formatDate(request.transferDate);
                            
                            return (
                                <TableRow
                                    key={request.id}
                                    sx={{
                                        '&:nth-of-type(odd)': {
                                            backgroundColor: 'secondary.100',
                                        },
                                        '&:hover': {
                                            backgroundColor: 'primary.50',
                                        },
                                }}
                            >
                                    <TableCell>
                                        {getStatusChip(request.reqstatus)}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: '0.85rem',
                                                fontWeight: 700,
                                                color: 'primary.main'
                                            }}
                                        >
                                            ₹{Number(request.consideredamount || 0).toFixed(2)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500, display: 'block' }}>
                                            {transformDateValue(request.creationTimestamp)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500, display: 'block' }}>
                                            {transformDateValue(request.transferdate)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="h6" color="text.secondary">
                                    No transfer requests found
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    // Mobile Card View
    const MobileRequestCard = ({ request }) => {
        const requestDateTime = formatDate(request.requestDate);
        const transferDateTime = formatDate(request.transferDate);
        
        return (
            <Card sx={{ mb: 2, boxShadow: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        {getStatusChip(request.reqstatus)}
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                Requested #
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                {/* ₹{Number(request.amount || 0).toFixed(2)} */}
                                {request.id}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, textAlign: 'left' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                Request Date
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, display: 'block' }}>
                                {transformDateValue(request.creationTimestamp)}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1, textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                Requested
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, display: 'block' }}>
                                ₹{Number(request.amount || 0).toFixed(2)}
                            </Typography>
                        </Box>

                        
                        {/* <Box sx={{ flex: 1, textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                Transfer Date
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, display: 'block' }}>
                                {transformDateValue(request.transferdate)}
                            </Typography>
                            
                        </Box> */}
                    </Box>

                {request.reqstatus==2 && (
                    <>
                <Divider sx={{ width: '100%', m: 1 }} />
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, textAlign: 'left' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                Considered [A]
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, display: 'block' }}>
                                ₹{Number(request.consideredamount || 0).toFixed(2)}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1, textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                Tax (2%) [B]
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, display: 'block' }}>
                                ₹{Number(request.taxdeducted || 0).toFixed(2)}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, textAlign: 'left' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                Transfered [A - B]
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, display: 'block' }}>
                                ₹{Number(request.transferedamount || 0).toFixed(2)}
                                
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1, textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                Transfered Date
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, display: 'block' }}>
                                {transformDateValue(request.transferdate)}
                            </Typography>
                        </Box>
                    </Box>
                    </>
                )}

                </CardContent>
            </Card>
        );
    };

    return (
        <Box>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container direction="row" spacing={2}>
                    {requestList && requestList.length > 0 ? (
                        requestList.map((request) => (
                            <Grid item xs={12} sm={6} md={4} lg={4} key={request.id}>
                                <MobileRequestCard key={request.id} request={request} />
                            </Grid>
                        ))
                    ) : (
                        <Card sx={{ textAlign: 'center', py: 4 }}>
                            <CardContent>
                                <Typography variant="h6" color="text.secondary">
                                    No transfer requests found
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Grid>
            )}
        </Box>
    );
}
